import type { DecoratorTypeValue } from "./providers/data/types";
import type { Options as ValueNodeSearchOptions } from "./providers/valueNodeSuggestionsProvider";
import type { UserShort } from "@/api";
import type { BlockType } from "@/components/doc/right-sidebar/create-blocks/utils/appendBlock";
import type { ExpressionNodePreviewType } from "@/components/expression/preview/types";
import type { IconName } from "@/components/icons/Icon";
import type { ConvertCandidate } from "@/input/utils/convertCandidates";
import type { DocumentModel } from "@/models/DocumentModel";
import type { GroupKeyFilter } from "@/models/NodeLookupModel";
import type { ConstantNodeModel } from "@/models/nodes/ConstantNodeModel";
import type { FunctionNodeModel } from "@/models/nodes/FunctionNodeModel";
import type { GroupNodeModel } from "@/models/nodes/GroupNodeModel";
import type { NumberNodeModel } from "@/models/nodes/NumberNodeModel";
import type { OperatorNodeModel } from "@/models/nodes/OperatorNodeModel";
import type { OperatorSymbolNodeModel } from "@/models/nodes/OperatorSymbolNodeModel";
import type { PlaceholderNodeModel } from "@/models/nodes/PlaceholderNodeModel";
import type { PlainTextNodeModel } from "@/models/nodes/PlainTextNodeModel";
import type { SymbolNodeModel } from "@/models/nodes/SymbolNodeModel";
import type { NodeModel, SuggestionIntlName } from "@/models/nodes/types";
import type { VariableNodeModel } from "@/models/nodes/VariableNodeModel";
import type { TailwindColor } from "@/utils/tailwindColors";
import type {
  Color,
  ClosingSymbol,
  FilterRule,
  FunctionNodeProps,
  InsertDecorator,
  InsertNode,
  LabelNodePosition,
  OpeningSymbol,
  OperatorTypeValue,
  PriorityRule,
  SuggestionLabelJson,
  SuggestionPopupGroup,
  SymbolNodeProps,
  SubstitutionRule,
  SuggestionAction,
  NodeCondition,
  SymbolSuggestionProps,
  LocaleKeywords,
  KeywordLang,
  FunctionPowerPlacement,
  NodeJson,
  PlaceholderVisible,
  PlaceholderAppearance,
  SympyActionType,
  OperatorSymbolNodeProps,
  InputMode,
} from "@corca/data";
import type { SympyCommand } from "@corca/sympy";

export type { SympyActionType };

export type SuggestionNodeType =
  | NumberNodeModel["type"]
  | VariableNodeModel["type"]
  | OperatorNodeModel["type"]
  | OperatorSymbolNodeModel["type"]
  | GroupNodeModel["type"]
  | ConstantNodeModel["type"]
  | FunctionNodeModel["type"]
  | PlainTextNodeModel["type"]
  | SymbolNodeModel["type"]
  | PlaceholderNodeModel["type"]
  | "expression"
  | "library"
  | "sympyAction"
  | "slashCommand"
  | "inlineTextStyle";

/**
 * Suggestion types that do not map to a concrete editor node (`SuggestionNodeType`).
 * Unlike node-backed suggestions (variables, operators, functions), these trigger an
 * action rather than inserting a node: `expression`/`sympyAction` run a SymPy hint,
 * `slashCommand` creates a block, `library` inserts a library item's nodes.
 */
export type NodelessSuggestionType =
  | "expression"
  | "library"
  | "sympyAction"
  | "slashCommand"
  | "inlineTextStyle";

const SLASH_COMMAND_BLOCK_TYPES = [
  "text",
  "heading1",
  "heading2",
  "heading3",
  "bulletList",
  "orderedList",
  "taskList",
  "blockquote",
  "callout",
  "code",
  "table",
  "horizontalRule",
] as const satisfies readonly BlockType[];

export type SlashCommandBlockType = (typeof SLASH_COMMAND_BLOCK_TYPES)[number];

export const SLASH_COMMAND_ALIGN_TYPES = ["alignLeft", "alignCenter", "alignRight"] as const;
export type SlashCommandAlignType = (typeof SLASH_COMMAND_ALIGN_TYPES)[number];

export type SlashCommandType = SlashCommandBlockType | SlashCommandAlignType;

export type FunctionArgument = {
  nodes?: ExpressionNodePreviewType[];
};

export type DecoratorProps = {
  name: DecoratorTypeValue;
};

export type VariableSuggestionProps = {
  color?: TailwindColor;
  arguments: FunctionArgument[];
  openingSymbol?: {
    key?: string;
    symbol: OpeningSymbol;
  };
  closingSymbol?: {
    key?: string;
    symbol: ClosingSymbol;
  };
};

export type ConstantSuggestionProps = {
  color?: TailwindColor;
};

// todo
export type FunctionSuggestionProps = {
  key?: string;
  functionView?: FunctionNodeProps["functionView"];
  isBuiltIn?: FunctionNodeProps["isBuiltIn"];
  powerPlacement?: FunctionPowerPlacement;
  parenthesesVisibility?: FunctionNodeProps["parenthesesVisibility"];
  color?: TailwindColor;
  arguments: FunctionArgument[];
  placement?: "prefix" | "postfix";
};

export type OperatorSuggestionProps = {
  key?: string;
  operatorType: OperatorTypeValue;
};

export type GroupSuggestionProps = {
  openingSymbol?: {
    key: string;
    symbol: OpeningSymbol;
  };
  closingSymbol?: {
    key: string;
    symbol: ClosingSymbol;
  };
  rows?: number;
  cols?: number;
  casesSystemEnabled?: boolean;
  omitParentheses?: boolean;
};

export type VariableFunctionSuggestionProps = {
  openingSymbol?: {
    key: string;
    symbol: OpeningSymbol;
  };
  closingSymbol?: {
    key: string;
    symbol: ClosingSymbol;
  };
};

export type PlaceholderSuggestionProps = {
  linkId?: string;
};

/**
 * Properties for suggestion data used in JSON files.
 */
export type SuggestionProps = {
  variable: VariableSuggestionProps;
  constant: ConstantSuggestionProps;
  function: FunctionSuggestionProps;
  operatorSymbol: OperatorSymbolNodeProps;
  number: never;
  group: GroupSuggestionProps;
  operator: OperatorSuggestionProps;
  plainText: never;
  variableFunction: VariableFunctionSuggestionProps;
  editNode: never;
  symbol: SymbolNodeProps;
  expression: never;
  library: never;
  placeholder: PlaceholderSuggestionProps;
  sympyAction: never;
  slashCommand: never;
  inlineTextStyle: never;
};

export type NodePlaceholders = {
  // not used anywhere yet, for future use
  labels?: {
    [key in LabelNodePosition]: PlaceholderAppearance;
  };
  children?: PlaceholderAppearance[];
  childrenVisible?: PlaceholderVisible[];
};

type ExpressionTarget = Extract<SuggestionAction, { type: "insert-expression" }>["targetNode"];

type SuggestionWithKeywords = "variable" | "constant" | "function" | "operatorSymbol" | "symbol";

type RequiredKeywords<T extends SuggestionNodeType = SuggestionNodeType> =
  T extends SuggestionWithKeywords
    ? {
        keywords: LocaleKeywords;
      }
    : object;

export type SuggestionItem<T extends SuggestionNodeType = SuggestionNodeType> = {
  key?: string;
  code?: string;
  type: T;
  intlName?: SuggestionIntlName;
  displayName?: string;
  symbol: string;
  symbolProps?: SymbolSuggestionProps;
  value?: string;
  category?: string | string[];
  keywords?: LocaleKeywords;
  variantKeywords?: LocaleKeywords;
  node?: T extends NodelessSuggestionType ? never : NodeModel<Exclude<T, NodelessSuggestionType>>;
  props?: SuggestionProps[T];
  labels?: SuggestionLabelJson[];
  decorators?: DecoratorProps[];

  /**
   * Indicates whether the suggestion should be rendered as a symbol
   */
  isSymbol?: boolean;
  /**
   * Indicates whether selecting this suggestion item should result in creating a new node (Nodes from JSON are bypassed.).
   * Used for constants, variables, and functions in a `valueSuggestionsProvider`.
   */
  isNewNode?: boolean;

  insertChildren?: (InsertNode[] | null)[];

  setChildren?: SubstitutionRule["setChildren"];

  insertDecorators?: InsertDecorator[];

  insertUnits?: (InsertNode | null)[];

  placeholders?: NodePlaceholders;

  nodesJson?: NodeJson[];

  /**
   * Where the cursor goes after a library item's `nodesJson` is inserted, and the target to try
   * when that one is missing.
   */
  targetNode?: ExpressionTarget;
  fallbackTargetNode?: ExpressionTarget;

  labelPlaceholderVisible?: PlaceholderVisible;

  isDifferential?: T extends "function" ? boolean : never;
} & RequiredKeywords<T>;

type PhantomHintBase = {
  label: string;
  usedWrapperIds?: string[];
  getHint: () => Promise<{ json: NodeJson; equals: boolean } | null>;
};

export type PhantomHint =
  | (PhantomHintBase & { type: "sympy"; command: SympyCommand; expressionText: string })
  | (PhantomHintBase & { type: "sequence"; expressionText: string })
  | (PhantomHintBase & { type: "reference" });

export type TrackedHintType = SympyCommand | "sequence";

/** What `hints_suggestion_accepted` reports a hint as; reference previews go untracked. */
export function trackedHintType(hint: PhantomHint | null | undefined): TrackedHintType | undefined {
  if (hint?.type === "sympy") return hint.command;
  if (hint?.type === "sequence") return "sequence";
  return undefined;
}

type SuggestionBase = {
  popupGroup?: SuggestionPopupGroup;
  /**
   * Regulate suggestion position in suggestion box.
   * Increase it and suggestion appears higher.
   */
  priority: number;
  /**
   * Icon name for suggestion.
   * We render it in <SuggestionIcon /> component.
   */
  icon?: IconName;

  /**
   * Render suggestion icon even if the real node preview can be rendered.
   */
  forceIcon?: boolean;

  priorityRules?: PriorityRule[];

  filterRules?: FilterRule[];

  sidebarGroupKey?: string;

  phantomHints?: PhantomHint[];

  matchedString?: string;

  hotkey?: string;

  disabled?: boolean;

  /** Shown with a checkmark (e.g. an inline text style that's currently applied). */
  checked?: boolean;

  /** Leave the leading slot empty — no icon, no node preview. */
  hideIcon?: boolean;

  /**
   * Trailing state indicator, translated at render time. Providers return keys,
   * not strings. Unlike `hotkey` it is never filled in with a number shortcut.
   */
  indicatorIntlName?: SuggestionIntlName;

  /**
   * Grey subtitle beside the title, translated at render time.
   * Takes precedence over the item's `category`.
   */
  categoryIntlName?: SuggestionIntlName;

  /**
   * Opts this suggestion into the `suggestions_action_selected` event, and is the
   * `action` name reported there.
   */
  trackingId?: string;

  nodePreviewPlacement?: "icon" | "afterTitle";

  /**
   * don't show suggestion if there are no other suggestions (without this flag)
   */
  hideStandalone?: boolean;

  /**
   * Synonyms the context menu search matches on, beyond the words of the rendered title.
   * Built with `normalizeSuggestionKeywords`. Rows without an item carry them here.
   */
  keywords?: LocaleKeywords;

  /**
   * Indicates whether the operator can be autocompleted via keyboard when it is matched in suggestion.
   * Operator autocompletes when user types the next character.
   * It has a higher priority than `canAutocomplete` of the following character match.
   * The pattern is matched against the string before the cursor including last typed character.
   * @example
   * true - always autocompletes the operator
   * @example
   * { regex: "^[\\d.]+.$" } - autocompletes if operator is preceded with a number
   */
  canBeAutocompleted?: boolean | Partial<Record<"math" | "text", boolean | { regex: string }>>;

  /**
   * Indicates whether the operator can auto-complete the preceding match.
   * @example
   * true - always autocompletes the operator
   * @example
   * { regex: "^[\\d.]+$" } - autocompletes if operator is preceded with a number
   */
  canAutocomplete?:
    | boolean
    | Partial<Record<"math" | "text", boolean | { regex: string; editingNode?: NodeCondition }>>;
};

export type SingleSuggestion<T extends SuggestionNodeType = SuggestionNodeType> = SuggestionBase & {
  type: "single";
  item: SuggestionItem<T>;
  // onSelect?: (doc: DocumentModel) => void;
  substitution?: SubstitutionRule;
  action?: SuggestionAction;

  context?: ("single" | "multi")[];

  nestedKey?: string;

  /**
   * Render the whole `nestedKey` family as a single parent row instead of
   * promoting its top item alongside the parent.
   */
  nestedOnly?: boolean;

  inputMode?: InputMode;

  nestedItems?: SuggestionsGroup<T>[];
};

export type MultiSuggestion<T extends SuggestionNodeType = SuggestionNodeType> = SuggestionBase & {
  type: "multi";
  items: SuggestionItem<T>[];
};

export type EditNodeSuggestion = SuggestionBase & {
  type: "editNode";
};

export type MentionSuggestion = SuggestionBase & {
  type: "mention";
  user: UserShort;
};

export type ClipboardCommand =
  | "copy"
  | "cut"
  | "paste"
  | "duplicate"
  | "duplicate-at-cursor"
  | "reference-at-cursor";

export type ClipboardCommandSuggestion = SuggestionBase & {
  type: "clipboardCommand";
  command: ClipboardCommand;
};

/**
 * Commands acting on the selected computed node. `unwrap` turns it back into
 * plain editable nodes; `go-to-source` is reference-only and jumps to the
 * computed wrapper the reference reads from.
 */
export type ComputedCommand = "unwrap" | "go-to-source";

export type ComputedCommandSuggestion = SuggestionBase & {
  type: "computedCommand";
  command: ComputedCommand;
};

/**
 * Collapse or expand the collapsible block(s) targeted by the current selection.
 * - `positions` are the block start positions
 * - `collapsed` is their current state
 * - `lineRange` is the first/last line number covered, for the title.
 */
export type CollapseBlockSuggestion = SuggestionBase & {
  type: "collapseBlock";
  positions: number[];
  collapsed: boolean;
  lineRange: [number, number];
  hideStandalone?: boolean;
};

/**
 * Open a color picker for the current text selection (prose, via the `setColor` action).
 * `color` is the current color, for the swatch icon.
 */
export type ColorSuggestion = SuggestionBase & {
  type: "color";
  hideStandalone?: boolean;
} & ({ target: "text"; color: Color } | { target: "node"; color?: TailwindColor; nodeId: string });

export type SelectionPromptSuggestion = SuggestionBase & {
  type: "selectionPrompt";
  hideStandalone?: boolean;
};

export type Suggestion<T extends SuggestionNodeType = SuggestionNodeType> =
  | EditNodeSuggestion
  | ClipboardCommandSuggestion
  | ComputedCommandSuggestion
  | CollapseBlockSuggestion
  | ColorSuggestion
  | SelectionPromptSuggestion
  | MentionSuggestion
  | SingleSuggestion<T>
  | MultiSuggestion<T>;

export type NewCursor = { node: NodeModel; index?: number } | false;

export type SuggestionNode = [node: NodeModel, newCursor?: NewCursor];

export type SuggestionsGroup<T extends SuggestionNodeType = SuggestionNodeType> = {
  /**
   * Used for testing purposes
   */
  name: SuggestionPopupGroup;
  view: "list" | "grid";
  items: Suggestion<T>[];
  startIndex?: number;
};

export type SuggestionProviderType =
  | "variable"
  | "constant"
  | "function"
  | "operator"
  | "operatorSymbol"
  | "functionOperator"
  | "input"
  | "group"
  | "matrix"
  | "multi"
  | "plainText"
  | "variableFunction"
  | "editNode"
  | "decorator"
  | "unit"
  | "factor"
  | "phantom"
  | "expressionDecorator"
  | "expression"
  | "library"
  | "placeholder"
  | "serviceFunction"
  | "slashCommand"
  | "latexInput"
  | "computed"
  | "convertUnit"
  | "clipboardCommand"
  | "computedCommand"
  | "inlineTextStyle"
  | "collapseBlock"
  | "color"
  | "selectionPrompt"
  | "convertNode"
  | "notation"
  | "graph"
  | "variableArguments"
  | "powerValue"
  | "functionBrackets"
  | "insertMath"
  | "mention";

export type ProviderSearchOptions = Partial<{
  variable: ValueNodeSearchOptions;
  constant: ValueNodeSearchOptions;
  function: ValueNodeSearchOptions;
  convertUnit: { candidates: ConvertCandidate[] };
  mention: { scope: { chatUid: string } | { documentUid: string } };
  library: {
    /**
     * Also offer the items whose `scope` is `service`, which the default popup leaves out.
     */
    service?: boolean;
    /**
     * The surface's accept list, passed by the search itself: the provider serves the kinds of
     * node it names, and every kind when it is empty or names `library`.
     */
    accept?: SuggestionProviderType[];
    /**
     * Which of the document's nodes count as already existing, as the value-node providers take
     * it; a field editing a node leaves that node out, so an item with its symbol is still offered.
     */
    filterExistingNodes?: GroupKeyFilter;
  };
}>;

type SearchOptions<T extends SuggestionProviderType> = T extends keyof ProviderSearchOptions
  ? ProviderSearchOptions[T]
  : never;

export type SuggestionProvider<
  PT extends SuggestionProviderType = SuggestionProviderType,
  T extends SuggestionNodeType = SuggestionNodeType,
> = {
  type: PT;
  /**
   * Indicates whether the suggestion provider should be omitted in suggestion search.
   */
  omitByDefault?: boolean;
  /**
   * Whether this provider may still run when the selection sits in (or spans) a locked
   * block. Only providers whose suggestions never edit locked content set this — every
   * other provider is skipped entirely. Individual suggestions that would edit must
   * still disable themselves.
   */
  allowedWhenLocked?: boolean;
  search: (
    str: string,
    doc: DocumentModel,
    lang: KeywordLang,
    options?: SearchOptions<PT>,
    signal?: AbortSignal,
  ) => Array<Suggestion<T>[] | Promise<Suggestion<T>[]>>;
};

export type GetSuggestionNodeFunction<T extends SuggestionNodeType> = (
  doc: DocumentModel | undefined,
  suggestion: SingleSuggestion<T>,
  type: T,
) => SuggestionNode | undefined;

export type SelectSuggestionFn = (
  suggestion: Suggestion,
  e?: KeyboardEvent,
  isSelectedManually?: boolean,
) => void;
